import { useMemo, useState } from 'react';
import {
  Plus, FileText, Pencil, Trash2, MoreVertical, Search, Copy, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { can } from '@/types';
import {
  useTemplates, useDeleteTemplate, useCreateTemplate,
} from '@/hooks/useNewsletter';
import type { Template } from '@/types/newsletter';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { TemplateFormModal } from '@/components/newsletter/TemplateFormModal';

const CATEGORY_TONES: Record<string, 'neutral' | 'info' | 'brass' | 'success' | 'warning'> = {
  newsletter:    'brass',
  announcement:  'info',
  greeting:      'success',
  promotion:     'warning',
  transactional: 'neutral',
};

export function TemplatesPage() {
  const { user } = useAuth();
  const canWrite = user ? can(user.role, 'newsletter', 'write') : false;

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const { data, isLoading, error } = useTemplates();
  const del = useDeleteTemplate();
  const create = useCreateTemplate();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Template | null>(null);
  const [deleting, setDeleting] = useState<Template | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.filter((t) => {
      if (t.archived) return false;
      if (category && t.category !== category) return false;
      if (search.trim()) {
        const s = search.toLowerCase();
        return `${t.name} ${t.description ?? ''}`.toLowerCase().includes(s);
      }
      return true;
    });
  }, [data, search, category]);

  if (isLoading) return <LoaderScreen />;
  if (error) {
    return (
      <Card>
        <EmptyState icon={AlertCircle} title="Could not load templates"
          description={(error as { message?: string })?.message ?? 'An unexpected error occurred.'} />
      </Card>
    );
  }

  async function confirmDelete() {
    if (!deleting) return;
    try {
      await del.mutateAsync(deleting.id);
      toast.success(`Deleted ${deleting.name}`);
      setDeleting(null);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Delete failed.');
    }
  }

  async function duplicate(t: Template) {
    try {
      await create.mutateAsync({
        name: `${t.name} (copy)`,
        description: t.description,
        category: t.category,
        locale: t.locale,
        subject: t.subject,
        previewText: t.previewText,
        blocks: t.blocks.map((b) => ({ ...b, id: crypto.randomUUID() })),
        theme: t.theme,
      });
      toast.success('Template duplicated');
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Duplicate failed.');
    }
  }

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[16rem]">
          <Input placeholder="Search templates…" leftIcon={<Search size={14} />}
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="w-44">
          <Select value={category} onChange={(e) => setCategory(e.target.value)}
            options={[
              { value: '', label: 'All categories' },
              { value: 'newsletter',    label: 'Newsletter' },
              { value: 'announcement',  label: 'Announcement' },
              { value: 'greeting',      label: 'Greeting' },
              { value: 'promotion',     label: 'Promotion' },
              { value: 'transactional', label: 'Transactional' },
            ]}
          />
        </div>
        {canWrite && (
          <Button leftIcon={<Plus size={13} />} onClick={() => setCreateOpen(true)}>
            New Template
          </Button>
        )}
      </div>

      {filtered.length === 0 ? (
        <Card>
          <EmptyState icon={FileText}
            title={search || category ? 'No templates match' : 'No templates yet'}
            description={search || category ? 'Try clearing filters.' : 'Templates save you time — design once, use across many campaigns.'}
            action={canWrite && !search && !category && (
              <Button leftIcon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
                Create First Template
              </Button>
            )}
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((t) => (
            <Card key={t.id} className="relative hover:border-brass-400/60 transition-colors">
              <div className="flex items-start justify-between gap-2 mb-3">
                <Badge tone={CATEGORY_TONES[t.category ?? 'newsletter']}>
                  {t.category ?? 'newsletter'}
                </Badge>
                {t.locale && t.locale !== 'en' && (
                  <Badge tone="neutral">{t.locale.toUpperCase()}</Badge>
                )}
                {canWrite && (
                  <div className="relative ml-auto">
                    <button onClick={() => setOpenMenu(openMenu === t.id ? null : t.id)}
                      className="p-1 text-text-dim hover:text-text hover:bg-bg-hover rounded-sm">
                      <MoreVertical size={14} />
                    </button>
                    {openMenu === t.id && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setOpenMenu(null)} />
                        <div className="absolute right-0 top-full mt-1 z-20 w-36 bg-bg-raised border border-border-strong rounded-sm shadow-raised py-1">
                          <button onClick={() => { setEditing(t); setOpenMenu(null); }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-text hover:bg-bg-hover text-left">
                            <Pencil size={12} /> Edit
                          </button>
                          <button onClick={() => { duplicate(t); setOpenMenu(null); }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-text hover:bg-bg-hover text-left">
                            <Copy size={12} /> Duplicate
                          </button>
                          <div className="my-1 border-t border-border" />
                          <button onClick={() => { setDeleting(t); setOpenMenu(null); }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-danger hover:bg-danger/10 text-left">
                            <Trash2 size={12} /> Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              <h3 className="font-display text-lg text-text truncate">{t.name}</h3>
              {t.description && (
                <p className="text-xs text-text-muted mt-1 leading-relaxed line-clamp-2">{t.description}</p>
              )}
              {t.subject && (
                <div className="mt-3 pt-3 border-t border-border text-2xs text-text-dim">
                  <span className="kicker">Default subject</span>
                  <div className="text-text mt-0.5 truncate">{t.subject}</div>
                </div>
              )}
              <div className="mt-3 text-2xs text-text-dim">
                {t.blocks?.length ?? 0} block{t.blocks?.length === 1 ? '' : 's'}
              </div>
            </Card>
          ))}
        </div>
      )}

      <TemplateFormModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <TemplateFormModal open={!!editing} onClose={() => setEditing(null)} template={editing} />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        loading={del.isPending}
        destructive
        title={`Delete ${deleting?.name}?`}
        confirmLabel="Delete template"
        description="Campaigns created from this template keep their own copy of the design."
      />
    </div>
  );
}
